import type { ComponentProps } from "react";
import React, { useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Icon,
  Select,
  Text,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import {
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import AdminSidebarLayout from "components/layout/AdminSidebarLayout/AdminSidebarLayout";
import {
  AthletesIcon,
  BlocksIcon,
  CustomersIcon,
  PaymentsIcon,
} from "components/Icons/Icons";
import { formatDate } from "shared/utils/dateFormater";
import { getPaths } from "lib/utils/paths";
import { useUser } from "src/contexts/useUser";

import BirthdaysCard from "./Birthdays/BirthdaysCard";
import RunningOutOfWorkoutsCard from "./RunningOutOfWorkouts/RunningOutOfWorkoutsCard";
import NewUploadsCard from "./NewUploads/NewUploadsCard";
import { DashboardProvider } from "src/contexts/DashboardContext";

/** Returns current date as a UTC-based Date (midnight UTC of today) */
const toUtcNow = (d: Date): Date => {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

const ShortcutBox = ({ icon, label, color, ...props }: ComponentProps<any>) => (
  <Box
    as="button"
    bg={color}
    px={4}
    py={5}
    flex={1}
    maxW={"300px"}
    _hover={{ shadow: "lg" }}
    transition="all ease 0.3s"
    {...props}
  >
    <HStack spacing={8}>
      <Icon color={"white"} fontSize="3xl" as={icon} />
      <Heading size="md" textTransform={"uppercase"} color={"white"}>
        {label}
      </Heading>
    </HStack>
  </Box>
);

export default function AdminHomePage() {
  const { user_name, user_type, isAdmin } = useUser();
  const router = useRouter();
  const t = useTranslations("pages.homePage");
  // eslint-disable-next-line i18next/no-literal-string
  const [timeFrame, setTimeFrame] = useState("this_week");
  

  const TIME_FRAMES: any = {
    this_week: {
      label: t("thisWeek"),
      time_frame: [
        toUtcNow(startOfWeek(new Date(), { weekStartsOn: 1 })),
        toUtcNow(endOfWeek(new Date(), { weekStartsOn: 1 }))
      ],
    },
    next_week: {
      label: t("nextWeek"),
      time_frame: [
        toUtcNow(startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 })),
        toUtcNow(endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }))
      ],
    },
    this_month: {
      label: t("thisMonth"),
      time_frame: [toUtcNow(startOfMonth(new Date())), toUtcNow(endOfMonth(new Date()))],
    },
    last_7_days: {
      label: t("last7Days"),
      time_frame: [toUtcNow(subWeeks(startOfDay(new Date()), 1)), toUtcNow(endOfDay(new Date()))],
    },
    last_14_days: {
      label: t("last14Days"),
      time_frame: [toUtcNow(subWeeks(startOfDay(new Date()), 2)), toUtcNow(endOfDay(new Date()))],
    },
    last_30_days: {
      label: t("last30Days"),
      time_frame: [toUtcNow(subDays(startOfDay(new Date()), 30)), toUtcNow(endOfDay(new Date()))],
    },
  };

  const [startDate, endDate] = useMemo(() => TIME_FRAMES[timeFrame].time_frame, [timeFrame]);


  return (
    <DashboardProvider startDate={startDate} endDate={endDate}>
      <AdminSidebarLayout>
        <VStack flexGrow={1} w="full" p={8} spacing={8} align="stretch">
          <Heading>
            {t("welcome", { name: user_name || user_type })}
          </Heading>
          {isAdmin && (
            <VStack align="stretch" spacing={8}>
              <Wrap spacing={5}>
                <ShortcutBox
                  icon={AthletesIcon}
                  label={t("athletes")}
                  onClick={() => router.push(getPaths.atletas.index())}
                  color="#E36D00"
                />
                <ShortcutBox
                  icon={BlocksIcon}
                  label={t("trainingBlocks")}
                  onClick={() => router.push(getPaths.treinos.blocos.index())}
                  color="#5386E4"
                />
                <ShortcutBox
                  icon={CustomersIcon}
                  label={t("coaches")}
                  onClick={() => router.push(getPaths.treinadores.index())}
                  color="#4C4B63"
                />
                <ShortcutBox
                  icon={PaymentsIcon}
                  label={t("subscriptions")}
                  onClick={() => router.push(getPaths.planosSubscricoes.subscricoes.index())}
                  color="#03B5AA"
                />
              </Wrap>
            </VStack>
          )}
          <VStack align="stretch" spacing={8}>
            <HStack justify={"space-between"}>
              <VStack flex={2} align={"flex-start"} spacing={1}>
                <Heading size={"md"}>{TIME_FRAMES[timeFrame].label}</Heading>
                <Text
                  fontWeight={"bold"}
                  fontSize={"sm"}
                  textTransform={"capitalize"}
                >
                  {formatDate(TIME_FRAMES[timeFrame].time_frame[0])} -{" "}
                  {formatDate(TIME_FRAMES[timeFrame].time_frame[1])}
                </Text>
              </VStack>

              <Select
                flex={1}
                onChange={e => setTimeFrame(e.target.value)}
                bg="white"
              >
                {Object.keys(TIME_FRAMES).map(tf => (
                  <option key={tf} value={tf}>
                    {TIME_FRAMES[tf].label}
                  </option>
                ))}
              </Select>
            </HStack>
            <HStack spacing={8} align={"stretch"}>
              <Box flex={1}>
                <RunningOutOfWorkoutsCard/>
              </Box>
            </HStack>
            <HStack spacing={8} align={"stretch"}>
              <Box flex={1}>
                <NewUploadsCard/>
              </Box>
              <Box flex={1}>
                <BirthdaysCard/>
              </Box>
            </HStack>
          </VStack>
        </VStack>
      </AdminSidebarLayout>
    </DashboardProvider>
  );
}
